import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { isAxiosError } from "axios";
import { Loader2Icon, LogOutIcon, ShieldCheckIcon } from "lucide-react";

import { AuthenticationAuthenticate } from "@/components/Authentication/Login/Authenticate";
import { AuthenticationForget } from "@/components/Authentication/Login/Forget";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  authClient,
  SessionPayload,
  tokenManager,
} from "@/lib/auth/token-manager";
import { request } from "@/lib/request";
import { captureEvent } from "@/lib/analytics";
import { User } from "@/lib/types";

const optional = z.string().optional().catch(undefined);

const searchSchema = z.object({
  client_id: optional,
  redirect_uri: optional,
  response_type: optional,
  scope: optional,
  state: optional,
  prompt: optional,
  code_challenge: optional,
  code_challenge_method: optional,
});

type Search = z.infer<typeof searchSchema>;

type Details = {
  client: { id: string; name: string };
  scope: string[];
  prompt: string;
};

export const Route = createFileRoute("/oauth/authorize")({
  validateSearch: searchSchema,
  component: RouteComponent,
});

const paramsOf = (search: Search) =>
  Object.fromEntries(
    Object.entries(search).filter(([, value]) => typeof value === "string"),
  ) as Record<string, string>;

function RouteComponent() {
  const search = Route.useSearch();
  const params = useMemo(() => paramsOf(search), [search]);

  const [details, setDetails] = useState<Details | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<"loading" | "signed-in" | "signed-out">(
    "loading",
  );
  const [view, setView] = useState<"login" | "forget">("login");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = useCallback(
    async (decision: "approve" | "deny" | "login_required") => {
      setBusy(true);
      setError(null);
      try {
        const client = decision === "approve" ? request : authClient;
        const response = await client.post("/oauth/authorize/decision", {
          ...params,
          decision,
        });
        captureEvent("onboarding_connect_succeeded", {
          service: "oauth_login",
          flow: "oauth",
        });
        window.location.assign(response.data.data.redirect_to);
      } catch (err) {
        setBusy(false);
        setError(
          isAxiosError(err) && typeof err.response?.data?.message === "string"
            ? err.response.data.message
            : "We could not complete this request. Please try again.",
        );
      }
    },
    [params],
  );

  const loadUser = useCallback(async () => {
    if (!tokenManager.isSignedIn()) {
      setStatus("signed-out");
      return false;
    }
    try {
      const response = await request.get<{ data: User }>("/user/info");
      setUser(response.data.data);
      setStatus("signed-in");
      return true;
    } catch {
      setStatus("signed-out");
      return false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const response = await authClient.get("/oauth/authorize/details", {
          params,
        });
        if (cancelled) return;
        setDetails(response.data.data);
      } catch (err) {
        if (cancelled) return;
        setInvalid(
          isAxiosError(err) &&
            typeof err.response?.data?.error_description === "string"
            ? err.response.data.error_description
            : "This authorization request is not valid.",
        );
        return;
      }

      await tokenManager.ready;
      const signedIn = await loadUser();
      if (cancelled) return;

      // prompt=none never shows UI: answer straight away.
      if (search.prompt === "none") {
        void decide(signedIn ? "approve" : "login_required");
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [decide, loadUser, params, search.prompt]);

  const handleLogin = useCallback(
    async (session: SessionPayload) => {
      await tokenManager.applySession(session);
      await loadUser();
    },
    [loadUser],
  );

  const handleSwitchAccount = useCallback(async () => {
    setBusy(true);
    await tokenManager.logout();
    setUser(null);
    setStatus("signed-out");
    setBusy(false);
  }, []);

  if (invalid) {
    return (
      <Shell>
        <Alert variant="destructive" className="border-none">
          <AlertTitle>Cannot continue</AlertTitle>
          <AlertDescription>{invalid}</AlertDescription>
        </Alert>
      </Shell>
    );
  }

  return (
    <Shell>
      <Card className="border-border/60 bg-card/90 w-full max-w-lg shadow-2xl backdrop-blur">
        <CardHeader>
          <div className="mb-2 flex size-10 items-center justify-center rounded-full bg-emerald-500/10">
            <ShieldCheckIcon className="size-5 text-emerald-400" />
          </div>
          <CardTitle>
            {details
              ? `Connect ${details.client.name}`
              : "Authorize application"}
          </CardTitle>
          <CardDescription>
            {details
              ? `${details.client.name} is asking to use your UniDeck account.`
              : "Checking the request…"}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {status === "loading" || !details ? (
            <div className="flex justify-center py-6">
              <Loader2Icon className="text-muted-foreground size-6 animate-spin" />
            </div>
          ) : status === "signed-in" ? (
            <>
              <div className="border-border/60 bg-muted/20 rounded-lg border p-4">
                <p className="text-muted-foreground text-sm">Signed in as</p>
                <p className="text-lg font-semibold text-white">
                  {user?.displayName || user?.email}
                </p>
                {user?.email && user.displayName !== user.email ? (
                  <p className="text-muted-foreground text-sm">{user.email}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-2 text-sm">
                <p className="text-muted-foreground">
                  If you allow it, {details.client.name} can act as you in
                  UniDeck
                  {details.scope.length ? ` (${details.scope.join(", ")})` : ""}
                  . You can remove its access any time under Settings, then
                  Security.
                </p>
              </div>
              {error ? (
                <Alert variant="destructive" className="border-none">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}
            </>
          ) : view === "login" ? (
            <AuthenticationAuthenticate
              onLogin={handleLogin}
              goToForget={() => setView("forget")}
            />
          ) : (
            <AuthenticationForget goToLogin={() => setView("login")} />
          )}
        </CardContent>
        {status === "signed-in" && details ? (
          <CardFooter className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              className="w-full sm:flex-1"
              disabled={busy}
              onClick={() => decide("approve")}
            >
              {busy ? <Loader2Icon className="animate-spin" /> : null}
              Allow
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full sm:flex-1"
              disabled={busy}
              onClick={() => decide("deny")}
            >
              Deny
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full sm:w-auto"
              disabled={busy}
              onClick={handleSwitchAccount}
            >
              <LogOutIcon />
              Switch account
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background flex min-h-screen w-screen items-center justify-center px-4 py-8">
      {children}
    </div>
  );
}
